import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { fetchPublicTrainers } from '../widget-api/public-trainers';
import { useAsync } from '../widget-hooks/useAsync';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { hasWidgetAuthQuery, widgetAuthQueryFromSearchParams } from '../widget-lib/widget-auth-query';
import { mergeWidgetQueryString } from '../widget-lib/merge-widget-query';
import { PATH } from '../widget-lib/path-routes';
import { RichTextBlock } from '../widget-ui/rich-text-block';
import { MediaAvatar } from '../widget-ui/media-avatar';
import { Button } from '@domcrm/embed-ui';
import { AuthRequiredModal } from '../widget-ui/auth-required-modal';
import { fetchClientServices } from '../widget-api/client-services';
import { listMySubscriptions } from '../widget-api/my-subscriptions-list';
import { createPersonalSlot, createBooking } from '../widget-api/bookings';
import { fetchScheduleSlots, type ScheduleSlotListItem } from '../widget-api/schedule-slots';

export default function TrainerDetailPage() {
  const { trainerId: trainerIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const suffix = useQuerySuffix();
  const { orgSlug, widgetConfig, accessToken, organizationId } = useEmbedBootstrap();

  const trainerId = trainerIdParam != null ? Number.parseInt(trainerIdParam, 10) : NaN;
  const validId = Number.isFinite(trainerId) && trainerId > 0;

  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [bookingDate, setBookingDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [bookingTime, setBookingTime] = useState('');
  const [comment, setComment] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'subscription' | 'cash'>('online');
  const [selectedSubId, setSelectedSubId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [daySlots, setDaySlots] = useState<ScheduleSlotListItem[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState(() => {
    return widgetConfig?.branches?.[0]?.id ? String(widgetConfig.branches[0].id) : '';
  });

  useEffect(() => {
    if (widgetConfig?.branches?.length && !selectedBranchId) {
      setSelectedBranchId(String(widgetConfig.branches[0].id));
    }
  }, [widgetConfig, selectedBranchId]);

  const dateSliderDays = useMemo(() => {
    const list = [];
    const start = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      
      const weekdayStr = d.toLocaleDateString('ru-RU', { weekday: 'short' });
      const monthStr = d.toLocaleDateString('ru-RU', { month: 'short' }).replace('.', '');
      
      list.push({
        dateStr: d.toISOString().slice(0, 10),
        dayNum: d.getDate(),
        monthStr: monthStr[0].toUpperCase() + monthStr.slice(1),
        weekdayStr: weekdayStr[0].toUpperCase() + weekdayStr.slice(1),
      });
    }
    return list;
  }, []);

  const busyBlocks = useMemo(() => {
    const trainerSlots = daySlots.filter(s => {
      const tId = s.trainer_user_id ?? s.trainer_id;
      return tId === trainerId && s.is_available !== false;
    });

    return trainerSlots.map(s => {
      const [sh, sm] = s.start_time.split(':').map(Number);
      const [eh, em] = s.end_time.split(':').map(Number);
      const startMins = sh * 60 + sm;
      const endMins = eh * 60 + em;

      const startRow = Math.max(1, Math.floor((startMins - 480) / 30) + 1);
      const endRow = Math.min(29, Math.ceil((endMins - 480) / 30) + 1);

      return {
        id: s.id,
        name: s.service_name || 'Занято',
        type: s.service_group_type === 'group' ? 'Групповое' : 'Персональное',
        startRow,
        endRow,
        timeStr: `${s.start_time.slice(0, 5)} - ${s.end_time.slice(0, 5)}`
      };
    });
  }, [daySlots, trainerId]);

  const { data: services } = useAsync(() => {
    if (!organizationId) return Promise.resolve([]);
    return fetchClientServices({
      organizationId,
      branchId: selectedBranchId ? Number(selectedBranchId) : null,
      accessToken,
    });
  }, [organizationId, accessToken, selectedBranchId]);

  const { data: mySubscriptions } = useAsync(() => {
    if (!accessToken) return Promise.resolve([]);
    return listMySubscriptions(accessToken);
  }, [accessToken]);

  const activeSubs = mySubscriptions?.filter((sub) => sub.status === 'active') ?? [];

  useEffect(() => {
    if (!bookingDate || !validId || !organizationId) {
      setAvailableTimes([]);
      setDaySlots([]);
      return;
    }
    let active = true;
    setLoadingTimes(true);

    const generateTimeSlots = (existingSlots: Array<{ trainer_user_id?: number | null; trainer_id?: number | null; start_time: string; end_time: string }>) => {
      const slots: string[] = [];
      const service = services?.find(s => s.id === Number(selectedServiceId));
      const duration = service ? service.duration_minutes : 60;

      for (let hour = 8; hour < 22; hour++) {
        for (const min of [0, 30]) {
          const timeStr = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
          
          const overlap = existingSlots.some(s => {
            const tId = s.trainer_user_id ?? s.trainer_id;
            if (tId !== trainerId) return false;
            
            const [sh, sm] = s.start_time.split(':').map(Number);
            const [eh, em] = s.end_time.split(':').map(Number);
            const slotStart = sh * 60 + sm;
            const slotEnd = eh * 60 + em;
            
            const currentStart = hour * 60 + min;
            const currentEnd = currentStart + duration;
            
            return currentStart < slotEnd && currentEnd > slotStart;
          });
          
          if (!overlap) {
            slots.push(timeStr);
          }
        }
      }
      return slots;
    };

    fetchScheduleSlots({
      organizationId,
      branchId: null,
      fromDate: bookingDate,
      toDate: bookingDate,
      accessToken
    })
      .then(slots => {
        if (active) {
          const freeTimes = generateTimeSlots(slots || []);
          setAvailableTimes(freeTimes);
          setDaySlots(slots || []);
        }
      })
      .catch(() => {
        if (active) {
          setAvailableTimes(generateTimeSlots([]));
          setDaySlots([]);
        }
      })
      .finally(() => {
        if (active) setLoadingTimes(false);
      });

    return () => {
      active = false;
    };
  }, [bookingDate, trainerId, selectedServiceId, organizationId, accessToken, services, validId]);

  const handlePersonalBooking = async () => {
    if (!selectedServiceId) {
      setErrorMsg('Выберите услугу');
      return;
    }
    if (!bookingDate) {
      setErrorMsg('Выберите дату');
      return;
    }
    if (!bookingTime) {
      setErrorMsg('Выберите время');
      return;
    }
    if (paymentMethod === 'subscription' && !selectedSubId) {
      setErrorMsg('Выберите абонемент');
      return;
    }
    setErrorMsg(null);
    setSubmitting(true);

    try {
      const service = services?.find(s => s.id === Number(selectedServiceId));
      if (!service) {
        setErrorMsg('Услуга не найдена');
        setSubmitting(false);
        return;
      }

      // Calculate end time
      const [startH, startM] = bookingTime.split(':').map(Number);
      const startMins = startH * 60 + startM;
      const endMins = startMins + service.duration_minutes;
      const endH = Math.floor(endMins / 60) % 24;
      const endM = endMins % 60;
      const formatTime = (h: number, m: number) => 
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const endTimeStr = formatTime(endH, endM);

      // 1. Create personal slot
      const slotRes = await createPersonalSlot({
        trainer_user_id: trainerId,
        service_id: service.id,
        slot_date: bookingDate,
        start_time: bookingTime,
        end_time: endTimeStr
      }, accessToken!);

      // 2. Book client onto this slot
      const bookingRes = await createBooking({
        slot_id: slotRes.id,
        comment: comment || null,
        payment_method: paymentMethod,
        user_subscription_id: paymentMethod === 'subscription' ? Number(selectedSubId) : null,
        return_url: window.location.origin + '/embed' + PATH.bookings
      }, accessToken!);

      if (bookingRes.payment_url) {
        window.location.assign(bookingRes.payment_url);
      } else {
        navigate(`${PATH.bookings}${suffix}`);
      }
    } catch (err: unknown) {
      console.error(err);
      const msg = err instanceof Error ? err.message : (err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Произошла ошибка при бронировании');
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const hasAuthOrKey = Boolean(
    accessToken || hasWidgetAuthQuery(searchParams) || searchParams.get('embed_global') === '1'
  );
  const canFetch = Boolean(orgSlug && hasAuthOrKey);
  const qsKey = searchParams.toString();

  const { data: trainers, loading, error } = useAsync(
    () => {
      if (!orgSlug || !canFetch) return Promise.resolve([]);
      return fetchPublicTrainers(orgSlug, widgetAuthQueryFromSearchParams(searchParams), accessToken);
    },
    [orgSlug, canFetch, qsKey, accessToken]
  );

  const trainer = trainers?.find((t) => t.id === trainerId) ?? null;

  useWidgetSeo(
    trainer?.full_name ? `${trainer.full_name} — Тренер` : 'Тренер',
    'Информация о тренере, квалификация, специализация и запись на персональные тренировки.'
  );

  if (!validId) {
    return (
      <div className="w-full min-w-0">
        <p className="text-sm text-[var(--text-muted)]">Некорректный идентификатор тренера.</p>
      </div>
    );
  }

  if (!hasAuthOrKey) {
    return (
      <Navigate
        to={`${PATH.signIn}${suffix}`}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!orgSlug || !canFetch) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <p className="text-sm text-[var(--text-muted)]">
          Откройте карточку из списка тренеров по ссылке виджета с org_slug и ключом/подписью.
        </p>
        <Link className="text-sm text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.trainers}${suffix}`}>
          К списку тренеров
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-5 shadow-[var(--shadow-card)]">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            <div className="h-24 w-24 animate-pulse rounded-full bg-[var(--bg-soft)] sm:h-28 sm:w-28" />
            <div className="flex-1 space-y-2 pt-1">
              <div className="h-6 w-48 animate-pulse rounded bg-[var(--bg-soft)]" />
              <div className="h-4 w-32 animate-pulse rounded bg-[var(--bg-soft)]" />
              <div className="h-3 w-full animate-pulse rounded bg-[var(--bg-soft)]" />
              <div className="h-3 w-3/4 animate-pulse rounded bg-[var(--bg-soft)]" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-w-0">
        <p className="text-sm text-[var(--error-text)]">{error}</p>
      </div>
    );
  }

  if (!trainer) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <p className="text-sm text-[var(--text-muted)]">Тренер не найден в списке организации.</p>
        <Link className="text-sm text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.trainers}${suffix}`}>
          К списку тренеров
        </Link>
      </div>
    );
  }



  const name = trainer.full_name || `Тренер #${trainer.id}`;
  const branchName = widgetConfig?.branches?.[0]?.name ?? null;
  const scheduleQs = mergeWidgetQueryString(searchParams.toString(), { trainer_user_id: String(trainer.id) });

  return (
    <div className="space-y-4 w-full min-w-0">
      <Link
        className="inline-flex items-center gap-1 text-sm text-[var(--accent)] underline-offset-2 hover:underline"
        to={`${PATH.trainers}${suffix}`}
      >
        ← Все тренеры
      </Link>

      <div className="overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-[var(--shadow-card)]">
        <div className="flex flex-col items-center gap-4 p-5 sm:flex-row sm:items-start">
          <MediaAvatar
            photoUrl={trainer.photo_url}
            label={name}
            sizeClass="h-24 w-24 sm:h-28 sm:w-28"
            roundedClass="rounded-full ring-2 ring-[var(--border)]"
          />
          <div className="min-w-0 flex-1 space-y-1 text-center sm:text-left">
            <h1 className="text-xl font-bold tracking-tight text-[var(--text)]">{name}</h1>
            {trainer.specialization ? (
              <p className="text-sm font-medium text-[var(--accent)]">{trainer.specialization}</p>
            ) : null}
            {branchName ? (
              <p className="text-xs text-[var(--text-muted)]">{branchName}</p>
            ) : null}
          </div>
        </div>

        {(trainer.description || trainer.experience || trainer.education) ? (
          <div className="space-y-4 border-t border-[var(--border)] px-5 py-4">
            {trainer.description ? (
              <div>
                <h2 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">О тренере</h2>
                <RichTextBlock html={trainer.description} format={trainer.description_format} />
              </div>
            ) : null}
            {trainer.experience ? (
              <div>
                <h2 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Опыт</h2>
                <RichTextBlock html={trainer.experience} format="plain" />
              </div>
            ) : null}
            {trainer.education ? (
              <div>
                <h2 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Образование</h2>
                <RichTextBlock html={trainer.education} format="plain" />
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="border-t border-[var(--border)] px-5 py-4 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <Link
              to={`${PATH.schedule}${scheduleQs}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--accent)] underline-offset-2 hover:underline"
            >
              Расписание тренера →
            </Link>
            <Button
              type="button"
              variant={isBookingOpen ? 'secondary' : 'primary'}
              onClick={() => {
                if (!accessToken) {
                  setAuthModalOpen(true);
                } else {
                  setIsBookingOpen(!isBookingOpen);
                }
              }}
              className="w-full sm:w-auto text-xs py-2 px-4 rounded-xl"
            >
              {isBookingOpen ? 'Скрыть форму записи' : 'Персональная тренировка'}
            </Button>
          </div>

          {authModalOpen && (
            <AuthRequiredModal
              open={authModalOpen}
              onClose={() => setAuthModalOpen(false)}
              querySuffix={suffix}
              returnTo={`${PATH.trainers}/${trainer.id}`}
              title="Вход для записи"
              description="Запись на персональную тренировку доступна после входа или регистрации. Выберите действие ниже."
            />
          )}

          {isBookingOpen && accessToken && (
            <div className="mt-4 space-y-4 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)]/50 p-4 w-full min-w-0 overflow-hidden">
              <h3 className="text-sm font-bold text-[var(--text)]">Запись на персональную тренировку</h3>
              
              <div className="grid gap-3 sm:grid-cols-2 w-full min-w-0">
                {/* Выбор филиала (для персональной записи) */}
                {widgetConfig?.branches && widgetConfig.branches.length > 1 && (
                  <div className="space-y-1.5 sm:col-span-2 min-w-0">
                    <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Филиал</label>
                    <select
                      value={selectedBranchId}
                      onChange={(e) => {
                        setSelectedBranchId(e.target.value);
                        setSelectedServiceId('');
                        setBookingTime('');
                      }}
                      disabled={submitting}
                      className="w-full rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-sm text-[var(--text)] outline-none focus:border-[var(--accent)] disabled:opacity-50"
                    >
                      {widgetConfig.branches.map((b) => (
                        <option key={b.id} value={String(b.id)}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="space-y-1.5 sm:col-span-2 min-w-0">
                  <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Услуга</label>
                  <select
                    value={selectedServiceId}
                    onChange={(e) => setSelectedServiceId(e.target.value)}
                    disabled={submitting}
                    className="w-full rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-sm text-[var(--text)] outline-none focus:border-[var(--accent)] disabled:opacity-50"
                  >
                    <option value="">Выберите услугу...</option>
                    {services?.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.duration_minutes} мин · {s.price} ₽)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Горизонтальный выбор даты */}
                <div className="space-y-1.5 sm:col-span-2 min-w-0">
                  <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Дата</label>
                  <div className="flex gap-2 overflow-x-auto pb-2 pt-0.5 no-scrollbar scroll-smooth w-full min-w-0 overscroll-x-contain" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    {dateSliderDays.map((day) => {
                      const isSelected = bookingDate === day.dateStr;
                      return (
                        <button
                          key={day.dateStr}
                          type="button"
                          disabled={submitting}
                          onClick={() => {
                            setBookingDate(day.dateStr);
                            setBookingTime('');
                          }}
                          className={`flex h-[70px] w-[54px] shrink-0 flex-col items-center justify-center rounded-xl border transition-all active:scale-[0.95] cursor-pointer ${
                            isSelected
                              ? 'bg-[var(--accent-bg)] text-[var(--accent-fg)] border-[var(--accent-bg)] shadow-sm font-medium'
                              : 'bg-[var(--bg-elevated)] text-[var(--text)] border-[var(--border)] hover:border-[var(--text-muted)]'
                          }`}
                        >
                          <span className={`text-[9px] uppercase tracking-wide ${isSelected ? 'text-[var(--accent-fg)]' : 'text-[var(--text-muted)]'}`}>
                            {day.weekdayStr}
                          </span>
                          <span className="text-base font-bold leading-none my-1">{day.dayNum}</span>
                          <span className={`text-[9px] ${isSelected ? 'text-[var(--accent-fg)]' : 'text-[var(--text-muted)]'}`}>
                            {day.monthStr}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Вертикальная сетка расписания а-ля Google Calendar */}
                {bookingDate && (
                  <div className="space-y-2 sm:col-span-2 min-w-0">
                    <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                      Расписание и доступное время
                    </label>
                    
                    <div className="border border-[var(--border)] rounded-2xl bg-[var(--bg-elevated)] overflow-hidden shadow-sm w-full min-w-0">
                      {loadingTimes ? (
                        <div className="flex flex-col items-center justify-center py-12 text-sm text-[var(--text-muted)]" aria-busy="true">
                          <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-[var(--accent)] mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Загрузка расписания тренера…
                        </div>
                      ) : (
                        <div className="max-h-[400px] overflow-y-auto overflow-x-hidden pr-1 relative custom-scrollbar w-full min-w-0">
                          
                          {/* Единая сетка (колонки: 3.2rem под шкалу часов, 1fr под слоты) */}
                          <div className="relative grid w-full" style={{ gridTemplateColumns: '3.2rem 1fr', gridTemplateRows: 'repeat(28, 40px)', height: '1120px' }}>
                            
                            {/* Шкала часов (слева - Колонка 1) */}
                            {Array.from({ length: 14 }).map((_, idx) => {
                              const hour = 8 + idx;
                              const gridRowIndex = idx * 2 + 1;
                              return (
                                <div
                                  key={idx}
                                  style={{ gridColumn: 1, gridRow: `${gridRowIndex}` }}
                                  className="text-[11px] font-bold text-[var(--text-muted)] text-right pr-2.5 -mt-[9px] select-none z-10"
                                >
                                  {String(hour).padStart(2, '0')}:00
                                </div>
                              );
                            })}

                            {/* Фоновая сетка линий (справа - Колонка 2) */}
                            {Array.from({ length: 28 }).map((_, idx) => {
                              const isHour = idx % 2 === 0;
                              return (
                                <div
                                  key={idx}
                                  style={{ gridColumn: 2, gridRow: `${idx + 1}` }}
                                  className={`w-full h-full border-t ${
                                    isHour ? 'border-[var(--border)]' : 'border-[var(--border)]/30 border-dashed'
                                  }`}
                                />
                              );
                            })}

                            {/* Занятые слоты тренера (справа - Колонка 2) */}
                            {busyBlocks.map((block) => (
                              <div
                                key={block.id}
                                style={{ gridColumn: 2, gridRow: `${block.startRow} / ${block.endRow}` }}
                                className="absolute inset-x-1 p-2 rounded-lg bg-[var(--border)]/60 border border-[var(--border)] text-[var(--text-muted)] flex flex-col justify-center overflow-hidden z-10 leading-tight"
                              >
                                <span className="text-[11px] font-semibold truncate">{block.name}</span>
                                <span className="text-[9px] opacity-85 mt-0.5 font-medium truncate">
                                  {block.timeStr} · {block.type}
                                </span>
                              </div>
                            ))}

                            {/* Свободные кликабельные интервалы (справа - Колонка 2) */}
                            {Array.from({ length: 28 }).map((_, i) => {
                              const h = Math.floor((480 + i * 30) / 60);
                              const m = (480 + i * 30) % 60;
                              const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                              const isAvailable = availableTimes.includes(timeStr);

                              if (!isAvailable) return null;

                              return (
                                <button
                                  key={i}
                                  type="button"
                                  disabled={submitting}
                                  onClick={() => setBookingTime(timeStr)}
                                  style={{ gridColumn: 2, gridRow: `${i + 1} / ${i + 2}` }}
                                  className="absolute inset-0 w-full h-full text-left px-3 text-[10px] text-[var(--accent)] font-semibold hover:bg-[var(--accent)]/10 focus:bg-[var(--accent)]/15 transition-all z-20 cursor-pointer flex items-center justify-start group"
                                  title={`Записаться на ${timeStr}`}
                                >
                                  <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[var(--accent-bg)] text-[var(--accent-fg)] px-1.5 py-0.5 rounded shadow-sm scale-95 origin-left duration-200">
                                    Выбрать {timeStr}
                                  </span>
                                </button>
                              );
                            })}

                            {/* Активный выбор клиента (справа - Колонка 2) */}
                            {bookingTime && (() => {
                              const [sh, sm] = bookingTime.split(':').map(Number);
                              const startMins = sh * 60 + sm;
                              const selectedIndex = (startMins - 480) / 30;
                              if (selectedIndex < 0 || selectedIndex > 27) return null;

                              const service = services?.find(s => s.id === Number(selectedServiceId));
                              const duration = service ? service.duration_minutes : 60;
                              const endMins = startMins + duration;
                              const startRow = selectedIndex + 1;
                              const endRow = startRow + (duration / 30);

                              const eh = Math.floor(endMins / 60) % 24;
                              const em = endMins % 60;
                              const endTimeStr = `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;

                              return (
                                <div
                                  style={{ gridColumn: 2, gridRow: `${startRow} / ${endRow}` }}
                                  className="absolute inset-x-1 p-2.5 rounded-lg bg-[var(--accent-bg)] text-[var(--accent-fg)] shadow-md flex flex-col justify-center overflow-hidden z-30 transition-all border border-[var(--accent-bg)]/80 leading-snug"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-[11px] font-bold truncate">
                                      {service?.name || 'Персональная тренировка'}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setBookingTime('');
                                      }}
                                      className="text-[var(--accent-fg)] text-base font-bold select-none cursor-pointer leading-none"
                                      title="Сбросить время"
                                    >
                                      ×
                                    </button>
                                  </div>
                                  <span className="text-[10px] text-[var(--accent-fg)] mt-0.5 font-bold">
                                    {bookingTime} — {endTimeStr} ({duration} мин)
                                  </span>
                                </div>
                              );
                            })()}

                          </div>
                        </div>
                      )}
                    </div>
                    {availableTimes.length === 0 && !loadingTimes && (
                      <p className="text-xs text-amber-600 font-medium">
                        В этот день у тренера нет доступного времени для выбранной услуги. Пожалуйста, выберите другой день.
                      </p>
                    )}
                    {bookingTime && (
                      <p className="text-xs text-[var(--text-muted)] font-medium">
                        Вы выбрали время начала: <strong className="text-[var(--text)]">{bookingTime}</strong>
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-1.5 sm:col-span-2 min-w-0">
                  <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Способ оплаты</label>
                  <select
                    value={paymentMethod}
                    disabled={submitting}
                    onChange={(e) => setPaymentMethod(e.target.value as 'online' | 'subscription' | 'cash')}
                    className="w-full rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-sm text-[var(--text)] outline-none focus:border-[var(--accent)] disabled:opacity-50"
                  >
                    <option value="online">Онлайн-оплата</option>
                    <option value="subscription">Абонемент</option>
                    <option value="cash">Оплата в студии</option>
                  </select>
                </div>

                {paymentMethod === 'subscription' && (
                  <div className="space-y-1.5 sm:col-span-2 min-w-0">
                    <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Выберите абонемент</label>
                    {activeSubs.length === 0 ? (
                      <p className="text-xs text-[var(--error-text)]">У вас нет активных абонементов.</p>
                    ) : (
                      <select
                        value={selectedSubId}
                        disabled={submitting}
                        onChange={(e) => setSelectedSubId(e.target.value)}
                        className="w-full rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-sm text-[var(--text)] outline-none focus:border-[var(--accent)] disabled:opacity-50"
                      >
                        <option value="">Выберите абонемент...</option>
                        {activeSubs.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.plan_name} (осталось: {sub.visits_total != null ? `${sub.visits_total - sub.visits_used} виз.` : 'безлимит'})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                <div className="space-y-1.5 sm:col-span-2 min-w-0">
                  <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Комментарий</label>
                  <textarea
                    value={comment}
                    disabled={submitting}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Ваши пожелания к тренировке..."
                    className="w-full rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-sm text-[var(--text)] outline-none focus:border-[var(--accent)] disabled:opacity-50 min-h-[60px]"
                  />
                </div>
              </div>

              {errorMsg && (
                <p className="text-xs text-[var(--error-text)] mt-2 font-medium">{errorMsg}</p>
              )}

              <div className="mt-3 flex justify-end">
                <Button
                  type="button"
                  disabled={submitting}
                  onClick={handlePersonalBooking}
                  className="rounded-xl px-6 py-2"
                >
                  {submitting ? 'Запись...' : 'Записаться'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
